import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { postsAPI } from '../api/posts';
import GridItem from '../components/GridItem';
import './ExplorePage.css';

const ExplorePage = () => {
  const observerTarget = useRef(null);
  const [columns, setColumns] = useState(3);
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();

  // 인기 해시태그 조회
  const { data: popularHashtags } = useQuery(
    'popularHashtags',
    () => postsAPI.getPopularHashtags(10),
    {
      staleTime: 10 * 60 * 1000, // 10분간 캐시
    }
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    'explore',
    ({ pageParam = 0 }) => postsAPI.getExplore(pageParam, 30),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.last) return undefined;
        return lastPage.pageable.pageNumber + 1;
      },
    }
  );

  // 해시태그 검색
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const tag = searchInput.trim().replace(/^#/, '');
    if (tag) {
      navigate(`/search?hashtag=${encodeURIComponent(tag)}`);
      setSearchInput('');
    }
  };

  // 해시태그 클릭
  const handleHashtagClick = (tag) => {
    navigate(`/search?hashtag=${encodeURIComponent(tag)}`);
  };

  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage]
  );

  useEffect(() => {
    const element = observerTarget.current;
    const option = { threshold: 0.5 };
    const observer = new IntersectionObserver(handleObserver, option);
    if (element) observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  // 반응형 컬럼 수 조정
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setColumns(2);
      else if (width < 1024) setColumns(3);
      else setColumns(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isLoading) {
    return <div className="explore-loading">로딩 중...</div>;
  }

  const items = data?.pages.flatMap((page) => page.content) || [];

  if (items.length === 0) {
    return (
      <div className="explore-empty">
        <p>탐색할 게시물이 없습니다.</p>
      </div>
    );
  }

  // Masonry 레이아웃: 아이템을 컬럼별로 분배
  const columnHeights = Array(columns).fill(0);
  const columnItems = Array.from({ length: columns }, () => []);

  items.forEach((item) => {
    // 가장 짧은 컬럼 찾기
    const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
    columnItems[shortestColumnIndex].push(item);
    // 높이 업데이트 (aspect ratio 기반)
    const aspectRatio = parseFloat(item.aspectRatio) || 1;
    columnHeights[shortestColumnIndex] += 1 / aspectRatio;
  });

  return (
    <div className="explore-container">
      {/* 검색창 */}
      <div className="explore-search-section">
        <form onSubmit={handleSearchSubmit} className="search-form">
          <input
            type="text"
            placeholder="해시태그 검색 (#태그 또는 태그)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">
            🔍
          </button>
        </form>

        {/* 인기 해시태그 */}
        {popularHashtags && popularHashtags.length > 0 && (
          <div className="popular-hashtags">
            <h3>인기 해시태그</h3>
            <div className="hashtag-chips">
              {popularHashtags.map((tag, index) => (
                <button
                  key={index}
                  className="hashtag-chip"
                  onClick={() => handleHashtagClick(tag)}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="masonry-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {columnItems.map((column, colIndex) => (
          <div key={colIndex} className="masonry-column">
            {column.map((item) => (
              <GridItem key={item.postId} item={item} />
            ))}
          </div>
        ))}
      </div>

      <div ref={observerTarget} className="scroll-trigger" />

      {isFetchingNextPage && (
        <div className="explore-loading-more">더 불러오는 중...</div>
      )}
    </div>
  );
};

export default ExplorePage;

